<script setup lang="ts">
import InputButton from '@/modules/ui/components/buttons/InputButton.vue';
import { computed, ref, watch } from 'vue';
import PlusThinIcon from '@/assets/icons/ui/plus-thin.svg';
import MinusThinIcon from '@/assets/icons/ui/minus-thin.svg';
import { numberFormatter } from '@/modules/common/utils/formatters.ts';
import { getLimit } from '@/modules/common/utils/limit.ts';
import { uniqueId } from 'lodash-es';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import UiInput, { type UiInputEmits } from '@/modules/ui/components/ui/UiInput.vue';

const model = defineModel<number | null>({ default: null });

const {
  id = uniqueId(IdPrefix.Control),
  required,
  disabled,
  min,
  max,
  maxLength,
  decimal = false,
} = defineProps<{
  id?: string;
  error?: boolean;
  placeholder?: string;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  maxLength?: number;
  decimal?: boolean;
  focusOnMount?: boolean;
}>();

const emit = defineEmits<UiInputEmits>();

const input = ref('');
const isFocused = ref(false);

const isDecreaseDisabled = computed(() => {
  if (disabled) {
    return true;
  }

  if (model.value === null) {
    return false;
  }

  if (maxLength !== undefined && model.value <= getLimit(maxLength, '-')) {
    return true;
  }

  return min !== undefined && model.value <= min;
});

const isIncreaseDisabled = computed(() => {
  if (disabled) {
    return true;
  }

  if (model.value === null) {
    return false;
  }

  if (maxLength !== undefined && model.value >= getLimit(maxLength, '+')) {
    return true;
  }

  return max !== undefined && model.value >= max;
});

watch(
  model,
  (value) => {
    if (decimal && isFocused.value) {
      return;
    }

    input.value = value !== null && Number.isFinite(value) ? numberFormatter.format(value) : '';
  },
  { immediate: true },
);

function parseDecimal(value: string) {
  const text = value.replace(/\s/g, '').replace(/,/g, '.');

  if (text === '' || text === '-') {
    return { text, number: null };
  }

  const number = Number(text);

  return { text, number: Number.isFinite(number) ? number : null };
}

function onInput(value: string) {
  if (decimal) {
    const parsed = parseDecimal(value);
    input.value = parsed.text;
    model.value = parsed.number;
    return;
  }

  model.value = value === '-' || value === '' ? null : +value.replace(/\s/g, '');
}

function adjust(value: number | null) {
  if (value === null) {
    return required ? 0 : null;
  }

  if (typeof min !== 'undefined' && value < min) {
    return min;
  }

  if (typeof max !== 'undefined' && value > max) {
    return max;
  }

  return value;
}

function onFocus(event: FocusEvent) {
  isFocused.value = decimal;
  emit('focus', event);
}

function onBlur(event: FocusEvent) {
  model.value = adjust(model.value);

  if (decimal) {
    isFocused.value = false;
    input.value = model.value !== null ? numberFormatter.format(model.value) : '';
  }

  emit('blur', event);
}

function decrease() {
  model.value = adjust(model.value === null ? -1 : model.value - 1);
}

function increase() {
  model.value = model.value === null ? min || adjust(1) : model.value + 1;
}
</script>

<template>
  <UiInput
    :id
    v-model="input"
    class="ui-input-number"
    :type="decimal ? 'text' : 'number'"
    align="center"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :error
    :max-length="decimal ? undefined : maxLength"
    :focus-on-mount="focusOnMount"
    no-clear
    @blur="onBlur"
    @clear="$emit('clear')"
    @focus="onFocus"
    @keydown="$emit('keydown', $event)"
    @update:model-value="onInput"
  >
    <template
      v-if="!decimal"
      #prefix
    >
      <InputButton
        v-if="!readonly"
        :disabled="isDecreaseDisabled"
        tabindex="-1"
        :error
        @click="decrease"
      >
        <MinusThinIcon class="icon" />
      </InputButton>
    </template>

    <template
      v-if="!decimal"
      #postfix
    >
      <InputButton
        v-if="!readonly"
        :disabled="isIncreaseDisabled"
        tabindex="-1"
        :error
        side="right"
        @click="increase"
      >
        <PlusThinIcon class="icon" />
      </InputButton>
    </template>
  </UiInput>
</template>

<style scoped lang="scss">
.icon {
  fill: var(--color-gray-600);
}
</style>
